/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date: Fri Jun 21 2023 14:28:10
 *
 *******************************************************/

// @flow
import React, { Component } from 'react';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Field } from 'redux-form';
import { TextStyle } from '@shopify/polaris';

import { RenderSelect3 } from 'src/components/views/common/forms/render-select-3';

import {
  PERSONALIZED_RECOMMENDATION,
  RECOMMENDATION_AI_BASED_FBT_PRIMARY_OPTIONS,
  RECOMMENDATION_AI_BASED_FBT_SECONDARY_OPTIONS,
  RECOMMENDATION_AI_BASED_PERSONALIZED_PRIMARY_OPTIONS,
  RECOMMENDATION_AI_BASED_PERSONALIZED_SECONDARY_OPTIONS,
  RECOMMENDATION_AI_BASED_RELATED_ALTERNATIVE_SECONDARY_OPTIONS,
  RECOMMENDATION_AI_BASED_RELATED_BOTH_SECONDARY_OPTIONS,
  RECOMMENDATION_AI_BASED_RELATED_COMPLEMENTARY_SECONDARY_OPTIONS,
  RECOMMENDATION_AI_BASED_RELATED_PRIMARY_OPTIONS,
  RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES
} from 'src/constants/recommendation';

import type { RecommendationWidgetType } from 'src/models/recommendation';

type State = {
  primaryAlg: Object,
  secondaryOptions: Array<Object>
};

type Props = {
  formValues: RecommendationWidgetType,
  primaryDisabled: boolean,
  primaryOptions: Array<any>,
  type: string,
  updateField: Function,
  primaryAlgorithm: string,
  secondaryAlgorithm: string
};

type ComposedProps = WithI18nProps & Props;

const PRIMARY_ALGORITHM_NAME = 'AIBasedRecommendProduct';
const SECONDARY_ALGORITHM_NAME = 'secondaryAlgorithm';

class AIBasedAlgorithm extends Component<ComposedProps, State> {
  state = {
    primaryAlg: { label: '', value: '' },
    secondaryOptions: []
  };

  componentDidMount() {
    const { i18n, type, updateField, primaryAlgorithm, secondaryAlgorithm } = this.props;

    // set label and value for primary algorithm
    let primaryValue = { label: '', value: '' };
    if (type === RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['frequently-bought-together'].value) {
      primaryValue =
        RECOMMENDATION_AI_BASED_FBT_PRIMARY_OPTIONS.find(item => item.value === primaryAlgorithm) ||
        RECOMMENDATION_AI_BASED_FBT_PRIMARY_OPTIONS[0];
    } else if (type === RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['related-items'].value) {
      primaryValue =
        RECOMMENDATION_AI_BASED_RELATED_PRIMARY_OPTIONS.find(
          item => item.value === primaryAlgorithm
        ) || RECOMMENDATION_AI_BASED_RELATED_PRIMARY_OPTIONS[0];
    } else if (
      type === RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES[PERSONALIZED_RECOMMENDATION].value
    ) {
      primaryValue =
        RECOMMENDATION_AI_BASED_PERSONALIZED_PRIMARY_OPTIONS.find(
          item => item.value === primaryAlgorithm
        ) || RECOMMENDATION_AI_BASED_PERSONALIZED_PRIMARY_OPTIONS[0];
    }
    primaryValue = { ...primaryValue, label: i18n.translate(primaryValue?.label) };
    this.changePrimaryAlgorithm(PRIMARY_ALGORITHM_NAME, primaryValue);

    // set label and value for secondary algorithm
    let secondaryValue = { label: 'Bestsellers', value: 'bestsellers' };
    if (secondaryAlgorithm) {
      secondaryValue = RECOMMENDATION_AI_BASED_PERSONALIZED_SECONDARY_OPTIONS.find(
        item => item.value === secondaryAlgorithm
      );
      secondaryValue = { ...secondaryValue, label: i18n.translate(secondaryValue?.label) };
    }

    updateField(SECONDARY_ALGORITHM_NAME, secondaryValue);
  }

  render() {
    const { i18n, primaryDisabled, primaryOptions, type } = this.props;
    const { secondaryOptions, primaryAlg } = this.state;

    const primaryHelper = RECOMMENDATION_AI_BASED_RELATED_PRIMARY_OPTIONS.find(
      item => item.value === primaryAlg.value
    );

    const translatedPrimaryOptions =
      primaryOptions.map(item => ({ ...item, label: i18n.translate(item.label) })) || [];
    const translatedSecondaryOptions =
      secondaryOptions.map(item => ({ ...item, label: i18n.translate(item.label) })) || [];

    return (
      <div className="ai-base-algorithm">
        <div className="ai-based-primary-algorithm">
          <div>
            <p className="field-title">
              {i18n.translate('AdminHTML.Recommendation.WidgetItem.aiBased_PrimaryAlgorithm_Label')}
            </p>
            <p className="field-helper">
              {i18n.translate(
                'AdminHTML.Recommendation.WidgetItem.aiBased_PrimaryAlgorithm_Helper'
              )}
            </p>
            <Field
              name={PRIMARY_ALGORITHM_NAME}
              component={RenderSelect3}
              options={translatedPrimaryOptions}
              isDisabled={primaryDisabled}
              value={primaryAlg}
              onChange={value => this.changePrimaryAlgorithm(PRIMARY_ALGORITHM_NAME, value)}
            />
            {type === RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['related-items'].value && (
              <div className="ai-based-primary-algorithm-helper-text">
                <TextStyle variation="subdued">
                  {primaryHelper ? i18n.translate(primaryHelper.helper) : ''}
                </TextStyle>
              </div>
            )}
          </div>
        </div>
        <div className="ai-based-secondary-algorithm">
          <p className="field-title">
            {i18n.translate('AdminHTML.Recommendation.WidgetItem.aiBased_SecondaryAlgorithm_Label')}
          </p>
          <p className="field-helper">
            {i18n.translate(
              'AdminHTML.Recommendation.WidgetItem.aiBased_SecondaryAlgorithm_Helper'
            )}
          </p>
          <Field
            name={SECONDARY_ALGORITHM_NAME}
            component={RenderSelect3}
            options={translatedSecondaryOptions}
            maxMenuHeight={300}
            onChange={value => this.changeSecondaryAlgorithm(SECONDARY_ALGORITHM_NAME, value)}
          />
        </div>
      </div>
    );
  }

  changePrimaryAlgorithm = (name: string, value: Record<string, string>) => {
    const { updateField } = this.props;
    updateField(name, value);
    updateField(SECONDARY_ALGORITHM_NAME, { label: 'Bestsellers', value: 'bestsellers' });

    let secondaryOptions = [];
    switch (value.value) {
      case 'FBT':
        secondaryOptions = RECOMMENDATION_AI_BASED_FBT_SECONDARY_OPTIONS;
        break;
      case PERSONALIZED_RECOMMENDATION:
        secondaryOptions = RECOMMENDATION_AI_BASED_PERSONALIZED_SECONDARY_OPTIONS;
        break;
      case 'alternative-products':
        secondaryOptions = RECOMMENDATION_AI_BASED_RELATED_ALTERNATIVE_SECONDARY_OPTIONS;
        break;
      case 'complementary-products':
        secondaryOptions = RECOMMENDATION_AI_BASED_RELATED_COMPLEMENTARY_SECONDARY_OPTIONS;
        break;
      case 'both':
        secondaryOptions = RECOMMENDATION_AI_BASED_RELATED_BOTH_SECONDARY_OPTIONS;
        break;
      default:
        break;
    }

    this.setState({ secondaryOptions, primaryAlg: value });
  };

  changeSecondaryAlgorithm = (name: string, value: Record<string, string>) => {
    const { updateField } = this.props;
    updateField(name, value);
  };
}

export default withI18n()(AIBasedAlgorithm);
